import React, { useEffect } from 'react';
import { ChangeEvent, FocusEvent, useState } from 'react';
import { IconButton, InputBaseProps, InputLabelProps, SxProps, Theme } from '@mui/material';
import MuiTextField from '@mui/material/TextField';
import { useVigoTranslation } from 'src/@core/hooks/useVigoTranslation';
import ArticleIcon from '@mui/icons-material/Article';
import { useAdditionalDataPanel } from 'src/@core/hooks/useAdditionalDataPanel';

export interface ITextAreaInput {
  label?: string;
  value?: string;
  placeholder?: string;
  sx?: SxProps<Theme>;
  helperText?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  maxRows?: number;
  minRows?: number;
  rows?: number;
  fullWidth?: boolean;
  name: string;
  error?: boolean;
  errormessage?: string;
  additionalDataOptions?: { id: string };
  InputProps?: InputBaseProps;
  readOnly?: boolean;
  inputLabelProps?: Partial<InputLabelProps>;
  required?: boolean;
  maxLength?: number;
}

export const VigoMultiLine = React.forwardRef(
  (
    {
      label,
      value,
      placeholder,
      sx,
      InputProps,
      helperText,
      onChange,
      onBlur,
      onFocus,
      maxRows,
      minRows,
      rows,
      fullWidth,
      name,
      error,
      errormessage,
      additionalDataOptions = undefined,
      readOnly,
      inputLabelProps,
      required = false,
      maxLength
    }: ITextAreaInput,
    ref
  ) => {
    const [textValue, setTextValue] = useState<string | undefined>(value);

    const { t } = useVigoTranslation();

    const { togglePanel } = useAdditionalDataPanel();

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      onChange?.(event);
      setTextValue(event.target.value);
    };

    const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
      onFocus?.(event);
    };

    useEffect(() => {
      setTextValue(value ?? '');
    }, [value]);

    return (
      <MuiTextField
        name={name}
        multiline
        label={t(label)}
        value={textValue}
        placeholder={t(placeholder)}
        inputProps={{ maxLength: maxLength }}
        sx={sx}
        size="small"
        variant="outlined"
        helperText={error ? t(errormessage) : t(helperText)}
        InputProps={{
          ...(Boolean(additionalDataOptions) && {
            endAdornment: (
              <IconButton onClick={() => togglePanel(additionalDataOptions?.id ?? '')}>
                <ArticleIcon />
              </IconButton>
            )
          }),
          readOnly: readOnly,
          ...InputProps
        }}
        InputLabelProps={inputLabelProps}
        error={error}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={handleFocus}
        maxRows={maxRows}
        minRows={minRows}
        rows={rows}
        fullWidth={fullWidth}
        inputRef={ref}
        required={required}
      />
    );
  }
);
